import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import { useLeague } from '../hooks/useLeague';
import { getLscArchiveItem, unpublishLscArchiveItem, LscApiError } from '../api';
import type { LscArchiveItem, LscVodCandidate } from '../types';
import ArchiveWatchVodSection from '../components/ArchiveWatchVodSection';
import ArchiveStatusBadge from '../components/ArchiveStatusBadge';
import AutopilotPublishedBadge, {
  AUTOPILOT_PUBLISHED_COPY,
  isAutopilotPublished,
} from '../components/AutopilotPublishedBadge';
import UnpublishArchiveConfirmModal from '../components/UnpublishArchiveConfirmModal';
import LoadingState from '../components/LoadingState';
import ErrorBanner from '../components/ErrorBanner';
import LscMutationErrorAlert from '../components/LscMutationErrorAlert';
import { parseLscMutationError, type ParsedLscMutationError } from '../entitlementBillingErrors';
import { lscPath, lscReturnTo } from '../routing';
import {
  formatArchiveCardMeta,
  formatArchiveMatchup,
  formatArchiveStageLabel,
  getArchiveDisplayTitle,
} from '../components/leagueHome/leagueHomeUtils';
import TeamMatchupBadge from '../components/leagueHome/TeamMatchupBadge';
import CfbMatchupChips from '../components/CfbMatchupChips';
import StreamWatchPage from '../components/stream/StreamWatchPage';
import { shouldRenderStreamPages } from '../stream/streamPages';
import { useLscContentColumn } from '../lscContentColumn';

const LscArchiveDetailPage: React.FC = () => {
  const { leagueId, archiveId: rawArchiveId } = useParams<{
    leagueId: string;
    archiveId: string;
  }>();
  const archiveId = rawArchiveId ? decodeURIComponent(rawArchiveId) : '';
  return (
    <LscAuthGate
      returnTo={lscReturnTo(
        `/leagues/${leagueId}/archive/${encodeURIComponent(archiveId)}`
      )}
    >
      <ArchiveDetailSwitch archiveId={archiveId} />
    </LscAuthGate>
  );
};

function ArchiveDetailSwitch({ archiveId }: { archiveId: string }) {
  const location = useLocation();
  const { league, isOwner, isCommissioner } = useLeague();
  if (
    shouldRenderStreamPages({
      setting: league?.stream_pages,
      search: location.search,
      previewKey: 'view',
      viewer: { isOwner, isCommissioner },
    })
  ) {
    return <StreamWatchPage archiveId={archiveId} />;
  }
  return <ArchiveDetailContent archiveId={archiveId} />;
}

function formatDuration(seconds?: number | null): string {
  if (seconds == null) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatDetailDate(value?: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

function formatStreamedLabel(value?: string | null): string | null {
  if (!value) return null;
  try {
    const label = new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
    return `Streamed ${label}`;
  } catch {
    return null;
  }
}

function formatStreamSource(archive: LscArchiveItem): string {
  const provider = archive.stream_provider?.trim();
  if (provider) {
    return provider.charAt(0).toUpperCase() + provider.slice(1);
  }
  if (archive.embed_provider?.trim() === 'twitch') {
    return 'Twitch';
  }
  const embedProvider = archive.embed_provider?.trim();
  if (embedProvider) {
    return embedProvider.charAt(0).toUpperCase() + embedProvider.slice(1);
  }
  return '—';
}

function ArchiveDetailContent({ archiveId }: { archiveId: string }) {
  useLscContentColumn('narrow');
  const { leagueId } = useParams<{ leagueId: string }>();
  const { isCommissioner, league } = useLeague();
  const [archive, setArchive] = useState<LscArchiveItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ParsedLscMutationError | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const [unpublishSuccess, setUnpublishSuccess] = useState<{
    message: string;
    candidate?: LscVodCandidate | null;
  } | null>(null);

  const load = useCallback(async () => {
    if (!leagueId || !archiveId) return;
    setLoading(true);
    setError(null);
    setUnavailable(false);
    try {
      const data = await getLscArchiveItem(leagueId, archiveId);
      setArchive(data);
    } catch (err) {
      if (err instanceof LscApiError && (err.status === 403 || err.status === 404)) {
        setUnavailable(true);
      } else {
        setError(parseLscMutationError(err, 'Could not load archive item.'));
      }
    } finally {
      setLoading(false);
    }
  }, [leagueId, archiveId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUnpublish = async (note: string) => {
    if (!leagueId || !archiveId) return;
    setUnpublishing(true);
    setError(null);
    try {
      const result = await unpublishLscArchiveItem(leagueId, archiveId, {
        note: note || undefined,
      });
      setUnpublishSuccess({
        message: result.message ?? 'VOD unpublished and returned to review.',
        candidate: result.candidate,
      });
      setUnpublishModalOpen(false);
      await load();
    } catch (err) {
      setError(parseLscMutationError(err, 'Unpublish failed.'));
    } finally {
      setUnpublishing(false);
    }
  };

  if (loading) return <LoadingState message="Loading archive..." />;

  if (unavailable) {
    return (
      <div className="space-y-4" data-testid="lsc-archive-unavailable">
        <h1 className="text-2xl font-bold text-white">Archive</h1>
        <ErrorBanner message="This archive is not available." />
        <Link
          to={lscPath(`/leagues/${leagueId}/archive`)}
          className="text-primary-300 hover:text-primary-200 text-sm"
          data-testid="archive-detail-back-link"
        >
          ← Back to Archives
        </Link>
      </div>
    );
  }

  if (!archive) {
    return error ? <LscMutationErrorAlert error={error} /> : null;
  }

  const displayTitle = getArchiveDisplayTitle(archive);
  const metadataRow = [
    formatArchiveCardMeta(archive, league?.active_edition),
    formatStreamedLabel(archive.vod_created_at),
  ]
    .filter(Boolean)
    .join(' · ');
  const awayLabel = archive.match_summary?.away_side_label?.trim();
  const homeLabel = archive.match_summary?.home_side_label?.trim();
  const showMatchupBadges = Boolean(awayLabel && homeLabel);
  const seasonWeek = formatArchiveStageLabel(archive.match_summary, league?.active_edition);
  const matchupLabel = formatArchiveMatchup(archive.match_summary);
  const autopilotPublished = isAutopilotPublished(archive);

  return (
    <div className="space-y-8 max-w-5xl" data-testid="lsc-archive-detail-page">
      <header className="space-y-4">
        <Link
          to={lscPath(`/leagues/${leagueId}/archive`)}
          className="text-sm text-primary-300 hover:text-primary-200 inline-block"
          data-testid="archive-detail-back-link"
        >
          ← Back to Archives
        </Link>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {archive.archive_status === 'published' ? (
              <span
                className="inline-flex items-center rounded-full border border-primary-700/60 bg-primary-900/30 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary-200"
                data-testid="archive-published-badge"
              >
                Published Replay
              </span>
            ) : null}
            {autopilotPublished ? <AutopilotPublishedBadge /> : null}
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-white" data-testid="archive-detail-title">
            {displayTitle}
          </h1>

          {metadataRow ? (
            <p className="text-sm text-secondary-400" data-testid="archive-detail-meta">
              {metadataRow}
            </p>
          ) : null}

          {showMatchupBadges ? (
            <div
              className="flex flex-wrap items-center justify-center gap-4 sm:justify-start pt-1"
              data-testid="archive-detail-matchup-badges"
            >
              <TeamMatchupBadge label={awayLabel!} side="away" />
              <span className="text-secondary-500 text-sm font-medium" aria-hidden>
                @
              </span>
              <TeamMatchupBadge label={homeLabel!} side="home" />
            </div>
          ) : null}
        </div>
      </header>

      <LscMutationErrorAlert error={error} onDismiss={() => setError(null)} />

      {unpublishSuccess && (
        <div
          className="rounded-lg border border-green-700 bg-green-900/30 p-4 text-sm space-y-3"
          data-testid="unpublish-success-banner"
        >
          <p className="text-green-100">{unpublishSuccess.message}</p>
          <div className="flex flex-wrap gap-4">
            <Link
              to={lscPath(`/leagues/${leagueId}/archive`)}
              className="text-primary-300 hover:text-primary-200"
            >
              Back to Archives
            </Link>
            <Link
              to={lscPath(`/leagues/${leagueId}/matches`)}
              className="text-primary-300 hover:text-primary-200"
              data-testid="unpublish-success-schedules-link"
            >
              Back to League Schedules
            </Link>
          </div>
        </div>
      )}

      <ArchiveWatchVodSection archive={archive} leagueId={leagueId!} />

      <div className="card max-w-lsc-narrow space-y-4" data-testid="archive-replay-details">
        <h2 className="text-lg font-semibold text-white">Replay details</h2>
        {autopilotPublished ? (
          <p
            className="rounded-lg border border-emerald-800/50 bg-emerald-900/20 p-3 text-sm text-emerald-100"
            data-testid="autopilot-published-explainer"
          >
            {AUTOPILOT_PUBLISHED_COPY}
          </p>
        ) : null}
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-secondary-500">Broadcaster</dt>
            <dd className="text-secondary-200">
              {archive.provider_broadcaster_login
                ? `@${archive.provider_broadcaster_login}`
                : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-secondary-500">Season / week</dt>
            <dd className="text-secondary-200">{seasonWeek ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-secondary-500">Teams</dt>
            <dd className="text-secondary-200">
              {league?.game_key === 'cfb' && awayLabel && homeLabel ? (
                <CfbMatchupChips
                  awayLabel={awayLabel}
                  homeLabel={homeLabel}
                  separator="@"
                  size="sm"
                  showLabels
                />
              ) : (
                matchupLabel
              )}
            </dd>
          </div>
          <div>
            <dt className="text-secondary-500">Published</dt>
            <dd className="text-secondary-200">{formatDetailDate(archive.published_at)}</dd>
          </div>
          <div>
            <dt className="text-secondary-500">Streamed</dt>
            <dd className="text-secondary-200">{formatDetailDate(archive.vod_created_at)}</dd>
          </div>
          <div>
            <dt className="text-secondary-500">Duration</dt>
            <dd className="text-secondary-200">{formatDuration(archive.duration_seconds)}</dd>
          </div>
          <div>
            <dt className="text-secondary-500">Source</dt>
            <dd className="text-secondary-200">{formatStreamSource(archive)}</dd>
          </div>
          <div>
            <dt className="text-secondary-500">Status</dt>
            <dd className="text-secondary-200">
              {archive.archive_status === 'published' ? (
                'Published'
              ) : isCommissioner && archive.archive_status ? (
                <ArchiveStatusBadge status={archive.archive_status} />
              ) : (
                '—'
              )}
            </dd>
          </div>
          {isCommissioner ? (
            <div>
              <dt className="text-secondary-500">Visibility</dt>
              <dd className="text-secondary-200 capitalize">
                {(archive.visibility ?? 'league_members').replace(/_/g, ' ')}
              </dd>
            </div>
          ) : null}
        </dl>

        {isCommissioner ? (
          <div
            className="flex flex-wrap items-center gap-4 border-t border-secondary-700 pt-4"
            data-testid="archive-commissioner-controls"
          >
            {archive.archive_status === 'published' ? (
              <button
                type="button"
                className="btn-secondary text-red-300 border-red-800 hover:bg-red-900/30"
                onClick={() => setUnpublishModalOpen(true)}
                disabled={unpublishing}
                data-testid="unpublish-archive-button"
              >
                Unpublish VOD
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <UnpublishArchiveConfirmModal
        open={unpublishModalOpen}
        onClose={() => setUnpublishModalOpen(false)}
        onConfirm={handleUnpublish}
        unpublishing={unpublishing}
      />
    </div>
  );
}

export default LscArchiveDetailPage;
